import { useEffect } from 'react';
import { Event } from '../lib/types';
import { getEventImage, getEventMediaType, getEventVideoUrl, getEventGifUrl, getEventPosterUrl } from '../lib/utils';

interface FullscreenImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
}

export default function FullscreenImageModal({ isOpen, onClose, event }: FullscreenImageModalProps) {
  const mediaType = getEventMediaType(event);
  const imageUrl = getEventImage(event);
  const videoUrl = getEventVideoUrl(event);
  const gifUrl = getEventGifUrl(event);
  const posterUrl = getEventPosterUrl(event);

  const getMediaUrl = () => {
    switch (mediaType) {
      case 'video':
        return videoUrl;
      case 'gif':
        return gifUrl;
      default:
        return imageUrl;
    }
  };

  const mediaUrl = getMediaUrl();

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4">
      <div className="relative w-full h-full flex items-center justify-center">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 bg-black bg-opacity-50 rounded-full p-2"
          aria-label="Close fullscreen view"
        >
          <svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M6 18L18 6M6 6l12 12" 
            />
          </svg>
        </button>

        {/* Media */}
        {mediaType === 'video' ? (
          <video
            className="max-w-full max-h-full object-contain"
            src={videoUrl}
            poster={posterUrl || undefined}
            preload="metadata"
            controls
            autoPlay
            muted
            loop
            playsInline
          />
        ) : (
          <img
            className="max-w-full max-h-full object-contain select-none"
            src={mediaUrl}
            alt={event.event_name}
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
    </div>
  );
}
